<template>
    <div v-if="dataLoaded">
        <div id="handonstable-container">
            <hot-table ref="reportInput" :settings="hotSettings">
                <hot-column
                    :title="$t('Reference')"
                    :settings="referenceSettings"
                ></hot-column>
                <hot-column
                    v-if="order.is_serial_number"
                    :title="$t('S/N')"
                    :settings="productSerialNumberSettings"
                >
                </hot-column>
                <hot-column
                    :title="$t('Product')"
                    :settings="productNameSettings"
                >
                </hot-column>
                <hot-column
                    :title="$t('Quantity')"
                    :settings="quantitySettings"
                ></hot-column>
                <hot-column
                    :title="$t('Comment')"
                    :settings="commentSettings"
                ></hot-column>
                <hot-column title="ID" :settings="rowIdSettings"></hot-column>
            </hot-table>
        </div>
        <b-modal
            ref="product-link-dialog"
            :title="$t('connect_products')"
            size="lg"
            :ok-title="$t('connect_products')"
            ok-only
            no-close-on-backdrop
            no-close-on-esc
            @ok="linkProductsToManufacturer"
            @close="deleteUnlinkedProducts"
        >
            <div class="d-block">
                <p>
                    The following products that you tried to add are not yet
                    linked to the manufacturer of this order ({{
                        order.manufacturer.name
                    }}):
                </p>
                <b-form>
                    <b-form-group>
                        <b-form-checkbox-group
                            id="checkbox-group-2"
                            stacked
                            v-model="selectedLinkProducts"
                            name="linkedRefs"
                        >
                            <b-form-checkbox
                                v-for="product in linkableProducts"
                                :key="product.product_id"
                                :value="product.product_id"
                            >
                                {{ product.collection }} {{ product.product }}
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                    </b-form-group>
                </b-form>
                <p>
                    Please select which products you want to link to
                    <strong>{{ order.manufacturer.name }}</strong
                    >. Unchecked products will be deleted from the order.
                </p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { HotTable, HotColumn } from "@handsontable/vue";
import Handsontable from "handsontable";
import "handsontable/dist/handsontable.full.css";
import axios from "axios";
import { mapActions, mapState, mapMutations } from "vuex";
import { memoizeDebounce } from "./../../plugins/utilities";
import moment from "moment";

export default {
  name: "OrdersEditOrder",
  components: {
    HotTable,
    HotColumn
  },
  props: {
    orderId: {
      type: String,
      default: null
    },
    adminMode: {
      type: Boolean,
      default: true
    }
  },
  data() {
    let ctx = this;
    return {
      unlinkedProducts: [],
      selectedLinkProducts: [],
      pendingReferences: [],
      autocompleteProduct: null,
      hotSettings: {
        data: [],
        comments: true,
        width: "100%",
        height: "auto",
        rowHeaders: true,
        colHeaders: true,
        selectionMode: "multi",
        invalidCellClassName: "invalidCell",
        viewportRowRenderingOffset: 50,
        currentRowClassName: "currentRow",
        beforeRemoveRow: this.beforeRemoveRow,
        columnSorting: true,
        minSpareRows: 1,
        enterMoves: {
          row: 0,
          col: 1
        },
        filters: true,
        dropdownMenu: ["filter_by_value", "filter_action_bar"],
        fillHandle: false,
        beforeChange: this.beforeChange,
        afterChange: this.afterChange,
        afterInit: this.afterInit,
        beforePaste: this.beforePaste,
        cells: function(row, col, prop) {
          return ctx.cellHandler(this, row, col, prop);
        },
        contextMenu: {
          items: {
            remove_row: {}
          }
        }
      },
      referenceSettings: {
        data: "reference",
        validator: function(value, cb) {
          ctx.referenceValidator(this, value, cb);
        },
        allowInvalid: false,
        allowEmpty: false
      },
      productNameSettings: {
        data: "product_name",
        type: "dropdown",
        source: function(query, process) {
          return ctx.fetchProducts(query, process);
        },
        strict: true,
        width: "350px",
        allowInvalid: false
      },
      productSerialNumberSettings: {
        data: "serial_number"
      },
      quantitySettings: {
        data: "quantity",
        type: "numeric",
        allowEmpty: false,
        allowInvalid: false,
        validator: Handsontable.validators.NumericValidator
      },
      commentSettings: {
        data: "comment",
        width: "220px"
      },
      rowIdSettings: {
        data: "id"
      },
      references: [],
      dataLoaded: false
    };
  },
  computed: {
    ...mapState({
      order: state => state.purchaseOrders.order,
      loading: state => state.purchaseOrders.order.loading,
      rows: state => state.purchaseOrders.rows,
      rowsLoaded: state => state.purchaseOrders.rowsLoaded
    }),
    linkableProducts() {
      return this.unlinkedProducts.map(p => {
        for (const ref in this.references) {
          if (this.references[ref].product_id === p) {
            return this.references[ref];
          }
        }
      });
    }
  },
  mounted() {
    this.getCollections().then(() => {
      this.hotSettings.data = this.rows;
      this.dataLoaded = true;
    });
  },
  methods: {
    fetchProducts(query, process) {
      if (query.length >= 2) {
        let references = this.references;

        references = Object.values(references)
          .filter(function(reference) {
            return reference.product
              .toLowerCase()
              .includes(query.toLowerCase());
          })
          .map(c => c.product);

        process(references);
      }
    },
    beforeRemoveRow(index, amount, physicalRows) {
      this.setSavingRow(true);
      physicalRows.forEach(i => {
        let rowId = this.hotInstance.getDataAtRowProp(i, "id");
        axios.delete(`/api/orders/${this.order.id}/rows/${rowId}`).then(() => {
          this.setLastSaveDate(moment());
          this.setSavingRow(false);
        });
      });
    },
    reloadOrder() {
      this.getOrder(this.order.id);
    },
    ...mapActions({
      getOrder: "purchaseOrders/getOrder",
      getRows: "purchaseOrders/getRows"
    }),
    ...mapMutations({
      setLastSaveDate: "purchaseOrders/lastSaveDate",
      setSavingRow: "purchaseOrders/savingRow"
    }),
    getCollections() {
      return new Promise(resolve => {
        axios
          .get(`/api/orders/${this.order.id}/available-references`)
          .then(resp => {
            this.references = resp.data;
            resolve();
          });
      });
    },
    afterInit() {
      this.hotInstance = this.$refs.reportInput.hotInstance;
      this.tweakTable();
    },
    tweakTable() {
      this.hotInstance.updateSettings({
        // Hide ID column
        hiddenColumns: {
          columns: [this.hotInstance.propToCol("id")]
        },
        readOnly: this.order.status !== "draft"
      });
    },
    cellHandler: function(ctx, row, col, prop) {
      // Make a cell readOnly if it has a reference and the reference exists...
      let cellProperties = {};

      if (prop === "quantity" && this.order.is_serial_number) {
        cellProperties.readOnly = true;
        cellProperties.renderer = this.defaultQuantityRenderer;
      }

      return cellProperties;
    },
    defaultQuantityRenderer(
      instance,
      td,
      row,
      col,
      prop,
      value,
      cellProperties
    ) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      Handsontable.dom.empty(td);
      Handsontable.dom.fastInnerText(td, 1);
      td.className = "not-found-product";
    },
    foundProductRenderer(instance, td, row, col, prop, value, cellProperties) {
      let ref = this.references[instance.getDataAtRowProp(row, "reference")];
      Handsontable.dom.fastInnerHTML(td, `${ref.product} (${ref.collection})`);
      td.className = "found-product";
    },
    notFoundProductRenderer(
      instance,
      td,
      row,
      col,
      prop,
      value,
      cellProperties
    ) {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      Handsontable.dom.empty(td);
      Handsontable.dom.fastInnerText(td, this.$t("product was not found"));
      td.className = "not-found-product";
    },
    beforeChange(changes, source) {
      if (source !== "edit" && source !== "CopyPaste.paste") {
        return;
      }

      let unlinkedProducts = [];
      let pendingReferences = [];

      changes.forEach(([row, prop, oldValue, newValue], index) => {
        // In the following cases, we dont want to do anything...
        if (oldValue === newValue || prop === "id") {
          return;
        }

        if (
          this.references[newValue] &&
          !this.references[newValue].manufacturer_linked &&
          !unlinkedProducts.includes(this.references[newValue].product_id)
        ) {
          pendingReferences.push(newValue);
          changes.splice(index, 1);
          unlinkedProducts.push(this.references[newValue].product_id);
        }
      });

      if (unlinkedProducts.length) {
        this.unlinkedProducts = unlinkedProducts;
        this.selectedLinkProducts = unlinkedProducts;
        this.pendingReferences = pendingReferences;
        this.$refs["product-link-dialog"].show();
      }
    },
    linkProductsToManufacturer() {
      this.linkProducts();
    },
    linkProducts() {
      let references = this.getReferencesForProductIds(
        this.selectedLinkProducts
      );
      // Update the references and set the 'manufacturer_linked to true'
      let rowData = [];
      references.forEach(r => {
        this.references[r].manufacturer_linked = true;
        if (this.pendingReferences.includes(r)) {
          rowData.push([r]);
        }
      });
      // Add products to the order
      this.hotInstance.populateFromArray(
        this.hotInstance.countRows() - 2,
        0,
        rowData
      );

      axios.post(`/api/link-products-to-manufacturer`, {
        manufacturer: this.order.manufacturer.id,
        products: this.selectedLinkProducts
      });
    },
    deleteUnlinkedProducts(e) {
      if (
        !confirm(
          "The products listed will not be added if you close this dialog. Are you sure you want to do this?"
        )
      ) {
        e.preventDefault();
      }
    },
    getReferencesForProductIds(productIds) {
      let refs = [];
      productIds.forEach(productId => {
        for (let ref in this.references) {
          if (
            this.references[ref].product_id === productId &&
            !refs.includes(ref)
          ) {
            refs.push(ref);
          }
        }
      });
      return refs;
    },
    afterChange(changes, source) {
      let i = this.hotInstance;
      if (
        source === "edit" ||
        source === "CopyPaste.paste" ||
        source === "populateFromArray"
      ) {
        changes.forEach(([row, prop, oldValue, newValue]) => {
          /// Problem category changed, need to reset the problem
          if (prop === "product_name") {
            let productData = Object.values(this.references).find(
              p => p.product === i.getDataAtRowProp(row, "product_name")
            );

            if (productData) {
              i.setDataAtRowProp(row, "reference", productData.reference);
            }
          }

          this.saveRow(row);
        });
      }
    },
    beforePaste(data, coords) {
      if (coords[0].startCol !== 0 || this.order.is_serial_number) {
        return true;
      }

      const lenBefore = data.length;

      let existingReferences = this.hotInstance.getData().map(d => d[0]);
      let references = data
        .map(d => d[0])
        .filter(i => !existingReferences.includes(i));
      let newItems = Array.from(new Set(references)).map(d => [d]);

      data.length = 0;
      [].push.apply(data, newItems);

      if (lenBefore !== data.length) {
        this.$bvModal.msgBoxOk(
          `Not all references were pasted because you tried to copy duplicate references or references that already exist in this PO`,
          {
            title: "Duplicate references",
            size: "sm",
            buttonSize: "sm",
            okVariant: "success"
          }
        );
      }

      if (data.length === 0) {
        return false;
      }
    },
    referenceValidator(ctx, value, cb) {
      if (value.length === 0) {
        cb(true);
        return;
      }

      const commentPlugin = this.hotInstance.getPlugin("comments");
      // Make sure the reference has not yet been added. Each reference can only occur once
      let existing = this.hotInstance
        .getData()
        .find((d, i) => i !== ctx.row && d[0] === value);

      if (!this.order.is_serial_number && existing !== undefined) {
        cb(false);
        commentPlugin.setCommentAtCell(
          ctx.row,
          ctx.col,
          `The reference '${value}' already exists and can't be added again`
        );
        commentPlugin.show();
      } else {
        cb(true);
        commentPlugin.removeCommentAtCell(ctx.row, ctx.col);
      }
    },
    handleReferenceInput(row, ref, prev) {},
    saveRow: memoizeDebounce(function(row) {
      let i = this.hotInstance;
      let id = i.getDataAtRowProp(row, "id");

      // If the ref is empty, dont save the row
      // If the ref is empty for an existing row, delete the row?

      let data = {
        reference: i.getDataAtRowProp(row, "reference"),
        comment: i.getDataAtRowProp(row, "comment"),
        quantity: i.getDataAtRowProp(row, "quantity")
          ? parseInt(i.getDataAtRowProp(row, "quantity"))
          : 0
      };

      if (this.order.is_serial_number) {
        data.serial_number = i.getDataAtRowProp(row, "serial_number");
        data.quantity = 1;
      }

      if (id !== null && data.reference) {
        this.updateRow(id, data);
      } else if (id !== null && !data.reference) {
        this.deleteRow(row);
      } else if (data.reference) {
        this.createRow(data, row);
      }
    }, 500),
    createRow(data, row) {
      let i = this.hotInstance;
      this.setSavingRow(true);
      return axios
        .post(`/api/orders/${this.order.id}/rows`, data)
        .then(r => {
          // Set the id on the row
          i.setDataAtRowProp(row, "id", r.data.data.id);
          this.setLastSaveDate(moment());
        })
        .finally(() => {
          this.setSavingRow(false);
        });
    },
    updateRow(id, data) {
      let i = this.hotInstance;
      this.setSavingRow(true);
      return axios
        .put(`/api/orders/${this.order.id}/rows/${id}`, data)
        .then(() => {
          this.setLastSaveDate(moment());
        })
        .finally(() => {
          this.setSavingRow(false);
        });
    },
    deleteRow(id, row) {}
  }
};
</script>

<style>
td.found-product {
  color: #659d74;
}

td.not-found-product {
  color: #a9a9a9;
}
</style>
